export default function JupiterDestinationsSection() {
  const stats = [
    {
      value: "46%",
      label: "of Jupiter requests are fishing charter or marine travel related with under 3 weeks notice",
    },
    {
      value: "2–4 weeks",
      label: "average notice for spring training road trips and baseball tournament travel",
    },
    {
      value: "Mar–Nov",
      label: "peak fishing season — passport renewals spike before Bahamas and Costa Rica charters",
    },
    {
      value: "36-hour",
      label: "shortest charter departure window we've accommodated for a Jupiter fishing client",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas",
      context: "The most common international destination for Jupiter travelers — particularly for fishing charters. Bimini is just 50 nautical miles offshore, and the Abacos, Berry Islands, and Exumas are regular multi-day charter destinations. Jupiter's fishing fleet runs extended trips to the Bahamas throughout the fishing season. All passengers on international fishing charters need valid passports.",
      urgency: "Charter trips are booked around weather windows and fish migration — passport issues discovered during trip prep create immediate crises",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Costa Rica",
      context: "Los Sueños Marina and Quepos on Costa Rica's Pacific coast draw Jupiter's serious sportfishing community for sailfish, marlin, and tarpon. These trips are typically planned during Jupiter's slower winter months (December–February) when the local offshore bite cools down. Costa Rica requires six months of passport validity for entry.",
      urgency: "Costa Rica trips are often confirmed with 2–4 weeks of notice when flights and marina slips align — too short for standard processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico",
      context: "Cancun, Riviera Maya, and Cabo San Lucas attract Jupiter families for beach vacations and fishing excursions. Business travelers also fly to Mexico City for corporate meetings. Mexico requires six months of passport validity for entry. Jupiter families often book spring break trips in January — passport discoveries spike in February.",
      urgency: "Spring break bookings surge in January — passport discoveries spike in February and March",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Canada",
      context: "Toronto and Montreal draw Jupiter business travelers for finance and healthcare conferences. Canadian ski destinations like Whistler and Banff attract winter sports enthusiasts. The St. Louis Cardinals play in Toronto annually, drawing Jupiter fans north for road games. Canada requires a valid passport for all air arrivals.",
      urgency: "Cardinals road trips to Toronto are announced in late winter — fans booking flights need current passports by spring",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "United Kingdom &amp; Europe",
      context: "London, Scotland, and Ireland are frequent destinations for Jupiter's affluent golf community — St. Andrews, Carnoustie, and Ballybunion draw serious golfers for bucket-list trips. Cultural travel to Paris, Rome, and Barcelona also attracts Jupiter's arts and business travelers. The Schengen Area requires three months of validity beyond your planned departure.",
      urgency: "Golf trip deposits and tour bookings are typically non-refundable with short cancellation windows",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Caribbean Cruises",
      context: "Jupiter residents regularly cruise from Port Everglades (Fort Lauderdale) and the Port of Palm Beach (Riviera Beach) to the Bahamas, Eastern Caribbean, and Southern Caribbean. Royal Caribbean, Carnival, Norwegian, and MSC all operate year-round itineraries. Every cruise passenger needs a valid passport, including children on family cruises.",
      urgency: "Cruise embarkation deadlines are immovable — passport issues discovered days before sailing require emergency processing",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Jupiter Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Jupiter
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Jupiter travelers head to the Bahamas, Costa Rica, the Caribbean, and Europe for a mix of fishing charters, baseball travel, cruises, and cultural experiences. These are the destinations that create the most passport urgency for our Jupiter clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout South Florida</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Palm Beach Gardens", href: "/cities/palm-beach-gardens" },
              { name: "West Palm Beach", href: "/cities/west-palm-beach" },
              { name: "Palm Beach", href: "/cities/palm-beach" },
              { name: "Boca Raton", href: "/cities/boca-raton" },
              { name: "Delray Beach", href: "/cities/delray-beach" },
              { name: "Naples", href: "/cities/naples" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}